import React from 'react';
import { CountdownTimer } from './CountdownTimer';
interface SlideFourProps {
  onSelectCTA: (option: string) => void;
  onBack: () => void;
  selectedOption: string | null;
  elapsedSeconds: number;
}
const OPTION_HEADLINES: Record<string, string> = {
  'Spend more time på fjellet 🏔️': 'How to get more time on the mountain',
  'Add one more patient': 'How to see more patients',
  'Stay on schedule': 'How to stay on schedule'
};
export function SlideFour({
  onSelectCTA,
  onBack,
  selectedOption,
  elapsedSeconds
}: SlideFourProps) {
  const headline = selectedOption && OPTION_HEADLINES[selectedOption] || 'How to get started';
  const keptPromise = elapsedSeconds <= 18;
  return <div className="flex flex-col h-full w-full justify-center animate-fade-in-up" data-id="element-91">
      {/* Back button */}
      <button onClick={onBack} className="self-start mb-4 flex items-center text-solventum-secondary hover:text-white transition-colors duration-200 text-sm font-body group" aria-label="Go back" data-id="element-92">
        <svg className="w-4 h-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-93">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-94" />
        </svg>
        Back
      </button>

      {/* Stopped timer + message */}
      <div className="flex flex-col items-center mb-8" data-id="element-95">
        <CountdownTimer elapsed={elapsedSeconds} stopped data-id="element-96" />
        <div className="mt-3 text-center" data-id="element-97">
          {keptPromise ? <p className="text-solventum-primary font-headline font-bold text-base" data-id="element-98">
              ✓ Promise kept! That took just {elapsedSeconds} seconds.
            </p> : <p className="text-solventum-secondary font-body text-sm" data-id="element-99">
              Sorry, that took a few more seconds — but the{' '}
              <span className="text-solventum-primary font-bold" data-id="element-100">
                18 minutes
              </span>{' '}
              you'll save are worth it!
            </p>}
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 text-center leading-tight" data-id="element-101">
        {headline}
      </h2>
      <p className="text-solventum-secondary font-body text-center mb-8" data-id="element-102">
        Choose the option that works best for you
      </p>

      <div className="flex flex-col space-y-4 w-full" data-id="element-103">
        {/* Option A: Web meeting */}
        <button onClick={() => onSelectCTA('web-meeting')} className="w-full bg-white/5 border-2 border-solventum-secondary hover:border-solventum-primary rounded-2xl p-6 text-left transition-all duration-300 hover:bg-white/10 group" data-id="element-104">
          <div className="flex items-start gap-4" data-id="element-105">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-solventum-primary/10 border border-solventum-primary/30 flex items-center justify-center" data-id="element-106">
              <svg className="w-6 h-6 text-solventum-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-107">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" data-id="element-108" />
              </svg>
            </div>
            <div className="flex-1" data-id="element-109">
              <h3 className="text-white font-headline font-bold text-lg mb-1 group-hover:text-solventum-primary transition-colors" data-id="element-110">
                Contact me for a short web meeting
              </h3>
              <p className="text-white/60 font-body text-sm" data-id="element-111">
                A quick 15-minute call to see how digital bonding fits your
                clinic
              </p>
            </div>
            <svg className="w-5 h-5 text-solventum-secondary group-hover:text-solventum-primary transition-all duration-200 group-hover:translate-x-0.5 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-112">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-113" />
            </svg>
          </div>
        </button>

        {/* Option B: Send material */}
        <button onClick={() => onSelectCTA('send-material')} className="w-full bg-white/5 border-2 border-solventum-secondary hover:border-solventum-primary rounded-2xl p-6 text-left transition-all duration-300 hover:bg-white/10 group" data-id="element-114">
          <div className="flex items-start gap-4" data-id="element-115">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-solventum-primary/10 border border-solventum-primary/30 flex items-center justify-center" data-id="element-116">
              <svg className="w-6 h-6 text-solventum-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-117">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" data-id="element-118" />
              </svg>
            </div>
            <div className="flex-1" data-id="element-119">
              <h3 className="text-white font-headline font-bold text-lg mb-1 group-hover:text-solventum-primary transition-colors" data-id="element-120">
                Send me material on getting started
              </h3>
              <p className="text-white/60 font-body text-sm" data-id="element-121">
                Receive info on how to get started with digital bonding at your
                clinic
              </p>
            </div>
            <svg className="w-5 h-5 text-solventum-secondary group-hover:text-solventum-primary transition-all duration-200 group-hover:translate-x-0.5 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-122">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-123" />
            </svg>
          </div>
        </button>
      </div>
    </div>;
}